import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import {
  buildProcessAnalytics,
  type ErrorTagId,
  type HoldBucketId,
  type ProcessAnalytics,
  type ProcessTrade,
  type StopComplianceKind,
} from '../lib/processAnalytics'

interface ProcessAnalysisSectionProps {
  trades: ProcessTrade[]
  currency: string
  formatMoney: (value: number, currency?: string, compact?: boolean) => string
}

function formatPct(value: number | null, digits = 1, signed = false): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = signed && value > 0 ? '+' : ''
  return `${sign}${value.toFixed(digits)}%`
}

function formatRr(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `1:${Math.abs(value).toFixed(1)}`
}

function formatR(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(2)}R`
}

function formatShare(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${Math.round(value * 100)}%`
}

function toneClass(value: number | null, positive = 'is-mint', negative = 'is-rose'): string {
  if (value == null) return ''
  if (value > 0) return positive
  if (value < 0) return negative
  return ''
}

function holdLabelKey(id: HoldBucketId): `analytics.process.${HoldBucketId}` {
  return `analytics.process.${id}`
}

function tagLabelKey(id: ErrorTagId): string {
  return `analytics.process.${id}`
}

function complianceLabelKey(kind: StopComplianceKind): string {
  return `analytics.process.${kind}`
}

export default function ProcessAnalysisSection({ trades, currency, formatMoney }: ProcessAnalysisSectionProps) {
  const { t } = useTranslation(['trading'])
  const analysis = useMemo(() => buildProcessAnalytics(trades), [trades])

  if (analysis.sampleCount === 0) {
    return (
      <section className="at-subpage at-analytics-page at-process-page">
        <ProcessHeader analysis={analysis} />
        <article className="at-card at-process-empty">
          <strong>{t('analytics.process.emptyTitle')}</strong>
          <span>{t('analytics.process.emptyDetail')}</span>
        </article>
      </section>
    )
  }

  return (
    <section className="at-subpage at-analytics-page at-process-page">
      <ProcessHeader analysis={analysis} />
      <div className="at-process-grid">
        <StopCard analysis={analysis} />
        <RDistributionCard analysis={analysis} />
        <CostCard analysis={analysis} currency={currency} formatMoney={formatMoney} />
        <HoldCard analysis={analysis} currency={currency} formatMoney={formatMoney} />
        <TagsCard analysis={analysis} currency={currency} formatMoney={formatMoney} />
      </div>
    </section>
  )
}

function ProcessHeader({ analysis }: { analysis: ProcessAnalytics }) {
  const { t } = useTranslation(['trading'])
  return (
    <header className="at-process-header">
      <div>
        <h2>{t('analytics.process.title')}</h2>
        <p>{t('analytics.process.subtitle')}</p>
      </div>
      {analysis.lowSample && (
        <span className="at-process-badge is-warn">
          {t('analytics.process.lowSample', { count: analysis.sampleCount })}
        </span>
      )}
    </header>
  )
}

function StopCard({ analysis }: { analysis: ProcessAnalytics }) {
  const { t } = useTranslation(['trading'])
  const { stop } = analysis
  const axisMax = Math.max(
    0.5,
    ...stop.largestDeviations.flatMap((row) => [row.plannedPct, row.realizedPct])
  )
  const insight = stop.plannedAvgPct != null && stop.realizedAvgPct != null && stop.excessLossShare != null
    ? t('analytics.process.stopInsight', {
        planned: formatPct(stop.plannedAvgPct),
        realized: formatPct(stop.realizedAvgPct),
        share: formatShare(stop.excessLossShare),
      })
    : t('analytics.process.stopInsightFallback')

  return (
    <article className="at-card at-process-card at-process-stop">
      <div className="at-process-card-head">
        <div>
          <strong>{t('analytics.process.stopTitle')}</strong>
          <p>{insight}</p>
        </div>
        <small>{t('analytics.process.planDiscipline')}</small>
      </div>
      <div className="at-process-stat-row">
        <div className="at-process-stat is-mint">
          <span>{t('analytics.process.plannedSl')}</span>
          <em>{formatPct(stop.plannedAvgPct)}</em>
        </div>
        <div className="at-process-stat is-rose">
          <span>{t('analytics.process.realizedSl')}</span>
          <em>{formatPct(stop.realizedAvgPct)}</em>
        </div>
        <div className="at-process-stat">
          <span>{t('analytics.process.deviation')}</span>
          <em>{formatPct(stop.deviationPct, 0, true)}</em>
        </div>
      </div>
      <div className="at-process-block">
        <div className="at-process-block-head">
          <span>{t('analytics.process.largestDeviations')}</span>
          <span className="at-process-legend">
            <i className="is-plan" /> {t('analytics.process.legendPlan')}
            <i className="is-real" /> {t('analytics.process.legendRealized')}
          </span>
        </div>
        {stop.largestDeviations.length ? (
          <div className="at-process-dumbbells">
            {stop.largestDeviations.map((row) => {
              const left = Math.min(row.plannedPct, row.realizedPct)
              const right = Math.max(row.plannedPct, row.realizedPct)
              return (
                <div key={row.id} className="at-process-dumbbell">
                  <strong>{row.symbol}</strong>
                  <div className="at-process-dumbbell-track">
                    <span
                      className="at-process-dumbbell-line"
                      style={{
                        left: `${(left / axisMax) * 100}%`,
                        width: `${((right - left) / axisMax) * 100}%`,
                      }}
                    />
                    <span className="at-process-dumbbell-dot is-plan" style={{ left: `${(row.plannedPct / axisMax) * 100}%` }} />
                    <span className="at-process-dumbbell-dot is-real" style={{ left: `${(row.realizedPct / axisMax) * 100}%` }} />
                  </div>
                  <small>{`${formatPct(row.plannedPct)} → ${formatPct(row.realizedPct)}`}</small>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="at-process-muted">{t('analytics.process.noChartData')}</p>
        )}
      </div>
      <div className="at-process-block">
        <div className="at-process-block-head">
          <span>{t('analytics.process.stopCompliance')}</span>
          <small>{stop.compliance.reduce((sum, row) => sum + row.count, 0)}</small>
        </div>
        {stop.compliance.some((row) => row.count > 0) ? (
          <>
            <div className="at-process-segments">
              {stop.compliance.filter((row) => row.count > 0).map((row) => (
                <span key={row.kind} className={`is-${row.kind}`} style={{ width: `${Math.max(row.share * 100, 4)}%` }} />
              ))}
            </div>
            <div className="at-process-segment-legend">
              {stop.compliance.map((row) => (
                <span key={row.kind}>
                  <i className={`is-${row.kind}`} />
                  {t(complianceLabelKey(row.kind))} · {row.count} ({formatShare(row.share)})
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="at-process-muted">{t('analytics.process.noChartData')}</p>
        )}
      </div>
    </article>
  )
}

function RDistributionCard({ analysis }: { analysis: ProcessAnalytics }) {
  const { t } = useTranslation(['trading'])
  const { rDistribution } = analysis
  const insight = rDistribution.losingCount > 0 && rDistribution.avgRealizedR != null
    ? t('analytics.process.rInsight', {
        beyond: rDistribution.losersBeyondPlan,
        losses: rDistribution.losingCount,
        avg: formatR(rDistribution.avgRealizedR),
      })
    : t('analytics.process.rInsightFallback')

  return (
    <article className="at-card at-process-card at-process-r">
      <div className="at-process-card-head">
        <div>
          <strong>{t('analytics.process.rTitle')}</strong>
          <p>{insight}</p>
        </div>
        <small>{t('analytics.process.tradeCount')}</small>
      </div>
      {rDistribution.sampleCount ? (
        <div className="at-process-r-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rDistribution.bars} margin={{ top: 18, right: 4, left: 4, bottom: 0 }} barCategoryGap="22%">
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#6d746f' }} />
              <Tooltip
                cursor={{ fill: 'rgba(34, 38, 36, 0.04)' }}
                contentStyle={{ borderRadius: 8, border: 0, background: '#202422', color: '#fff', fontSize: 12 }}
                formatter={(value) => [Number(value), t('analytics.process.tradeCount')]}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={28} label={{ position: 'top', fontSize: 10, fill: '#5f6561' }} shape={renderRBar} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="at-process-muted">{t('analytics.process.noChartData')}</p>
      )}
      <p className="at-process-plan-cap"><i /> {t('analytics.process.planCap')}</p>
      <div className="at-process-stat-row">
        <div className="at-process-stat">
          <span>{t('analytics.process.plannedRr')}</span>
          <em>{formatRr(rDistribution.plannedRr)}</em>
        </div>
        <div className="at-process-stat is-rose">
          <span>{t('analytics.process.realizedRr')}</span>
          <em>{formatRr(rDistribution.realizedRr)}</em>
        </div>
      </div>
    </article>
  )
}

function renderRBar(props: {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: { tone?: 'loss' | 'neutral' | 'gain'; isPlan?: boolean; count?: number }
}) {
  const x = props.x ?? 0
  const y = props.y ?? 0
  const width = props.width ?? 0
  const height = Math.max(props.height ?? 0, props.payload?.count ? 0 : 2)
  const fill = props.payload?.tone === 'gain' ? '#3d8b7a' : props.payload?.tone === 'loss' ? '#c45c55' : '#c5cac4'
  if (props.payload?.isPlan) {
    return <rect x={x} y={y} width={width} height={height} fill="#f4dddb" stroke="#c45c55" strokeDasharray="4 3" strokeWidth={1.4} rx={3} />
  }
  return <rect x={x} y={y} width={width} height={height} fill={fill} rx={3} />
}

function CostCard({
  analysis,
  currency,
  formatMoney,
}: {
  analysis: ProcessAnalytics
  currency: string
  formatMoney: ProcessAnalysisSectionProps['formatMoney']
}) {
  const { t } = useTranslation(['trading'])
  const { cost } = analysis
  const maxAbs = Math.max(1, ...cost.rows.map((row) => Math.abs(row.value)))
  const insight = cost.avgFee != null && cost.feeShareOfNet != null
    ? t('analytics.process.costInsight', {
        share: formatShare(cost.feeShareOfNet),
        avg: formatMoney(cost.avgFee, currency),
      })
    : t('analytics.process.costInsightFallback')
  const labels: Record<(typeof cost.rows)[number]['id'], string> = {
    gross: t('analytics.process.grossPnl'),
    commission: t('analytics.process.commission'),
    net: t('analytics.process.netPnlLabel'),
  }

  return (
    <article className="at-card at-process-card">
      <div className="at-process-card-head">
        <div>
          <strong>{t('analytics.process.costTitle')}</strong>
          <p>{insight}</p>
        </div>
        <small>{t('analytics.process.grossToNet')}</small>
      </div>
      <div className="at-process-hbars">
        {cost.rows.map((row) => (
          <div key={row.id} className="at-process-hbar">
            <span>{labels[row.id]}</span>
            <div className="at-process-hbar-track">
              <i
                className={row.value >= 0 ? 'is-mint' : 'is-rose'}
                style={{ width: `${Math.max((Math.abs(row.value) / maxAbs) * 100, row.value === 0 ? 0 : 6)}%` }}
              />
            </div>
            <em className={toneClass(row.value)}>{formatMoney(row.value, currency)}</em>
          </div>
        ))}
      </div>
      <div className="at-process-stat-row">
        <div className="at-process-stat">
          <span>{t('analytics.process.totalFees')}</span>
          <em>{formatMoney(cost.totalFees, currency)}</em>
        </div>
        <div className="at-process-stat">
          <span>{t('analytics.process.breakEvenMove')}</span>
          <em>{formatPct(cost.breakEvenMovePct, 2)}</em>
        </div>
      </div>
    </article>
  )
}

function HoldCard({
  analysis,
  currency,
  formatMoney,
}: {
  analysis: ProcessAnalytics
  currency: string
  formatMoney: ProcessAnalysisSectionProps['formatMoney']
}) {
  const { t } = useTranslation(['trading'])
  const { hold } = analysis
  const maxAbs = Math.max(1, ...hold.buckets.map((row) => Math.abs(row.pnl)))
  const insight = hold.durationRatio != null && hold.longHoldLossShare != null
    ? t('analytics.process.holdInsight', {
        ratio: hold.durationRatio.toFixed(1),
        share: formatShare(hold.longHoldLossShare),
      })
    : t('analytics.process.holdInsightFallback')

  return (
    <article className="at-card at-process-card">
      <div className="at-process-card-head">
        <div>
          <strong>{t('analytics.process.holdTitle')}</strong>
          <p>{insight}</p>
        </div>
        <small>{t('analytics.process.netPnl')}</small>
      </div>
      <div className="at-process-stat-row">
        <div className="at-process-stat is-mint">
          <span>{t('analytics.process.winnersHold')}</span>
          <em>
            {hold.winners
              ? t('analytics.process.duration', { days: hold.winners.days, hours: hold.winners.hours })
              : '—'}
          </em>
        </div>
        <div className="at-process-stat is-rose">
          <span>{t('analytics.process.losersHold')}</span>
          <em>
            {hold.losers
              ? t('analytics.process.duration', { days: hold.losers.days, hours: hold.losers.hours })
              : '—'}
          </em>
        </div>
      </div>
      <div className="at-process-hbars">
        {hold.buckets.map((row) => (
          <div key={row.id} className="at-process-hbar">
            <span>{t(holdLabelKey(row.id))}</span>
            <div className="at-process-hbar-track">
              <i
                className={row.pnl >= 0 ? 'is-mint' : 'is-rose'}
                style={{ width: `${Math.max((Math.abs(row.pnl) / maxAbs) * 100, row.count ? 6 : 0)}%` }}
              />
            </div>
            <em className={toneClass(row.pnl)}>{formatMoney(row.pnl, currency)}</em>
          </div>
        ))}
      </div>
      <p className="at-process-note">{t('analytics.process.holdNote')}</p>
    </article>
  )
}

function TagsCard({
  analysis,
  currency,
  formatMoney,
}: {
  analysis: ProcessAnalytics
  currency: string
  formatMoney: ProcessAnalysisSectionProps['formatMoney']
}) {
  const { t } = useTranslation(['trading'])
  const { tags } = analysis
  const maxAbs = Math.max(1, ...tags.rows.map((row) => Math.abs(row.pnl)))
  const top = tags.rows.filter((row) => row.id !== 'untagged').sort((a, b) => a.pnl - b.pnl)[0]
  const insight = top && tags.topLossShare != null
    ? t('analytics.process.tagsInsight', {
        tag: t(tagLabelKey(top.id)),
        share: formatShare(tags.topLossShare),
      })
    : t('analytics.process.tagsInsightFallback')

  return (
    <article className="at-card at-process-card">
      <div className="at-process-card-head">
        <div>
          <strong>{t('analytics.process.tagsTitle')}</strong>
          <p>{insight}</p>
        </div>
        <small>{t('analytics.process.oneTagPerTrade')}</small>
      </div>
      {tags.rows.length ? (
        <div className="at-process-tag-list">
          {tags.rows.map((row) => (
            <div key={row.id} className={`at-process-tag-row${row.id === 'untagged' ? ' is-muted' : ''}`}>
              <div>
                <strong>{t(tagLabelKey(row.id))}</strong>
                <small>{t('analytics.process.tagTrades', { count: row.count, avg: formatMoney(row.avgPnl, currency) })}</small>
              </div>
              <div className="at-process-hbar-track">
                <i
                  className={row.id === 'untagged' ? 'is-gray' : row.pnl >= 0 ? 'is-mint' : 'is-rose'}
                  style={{ width: `${Math.max((Math.abs(row.pnl) / maxAbs) * 100, 6)}%` }}
                />
              </div>
              <em className={row.id === 'untagged' ? 'is-gray' : toneClass(row.pnl)}>{formatMoney(row.pnl, currency)}</em>
            </div>
          ))}
        </div>
      ) : (
        <p className="at-process-muted">{t('analytics.process.noChartData')}</p>
      )}
      <p className="at-process-note">{t('analytics.process.untaggedNote')}</p>
    </article>
  )
}
